import { useEffect, useMemo, useState } from 'react';
import { LockSimpleIcon, InfoIcon } from '@phosphor-icons/react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchBar } from '@/components/ui/search-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useGroups } from '@/lib/hooks/useGroups';
import {
  useSetUserGroupMemberships,
  useUserGroupMemberships,
} from '@/lib/hooks/useMemberships';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getOrderedRoleKeys } from '@/lib/utils/roles.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { cn } from '@/lib/utils';
import type { UserProfile, UserRole } from '@/lib/types/database';
import type { ManageGroup } from '@/lib/types/groups.types';

export interface UserGroupsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserProfile | null;
}

export function UserGroupsDrawer({
  open,
  onOpenChange,
  user,
}: UserGroupsDrawerProps) {
  const userId = user?.id ?? null;
  const userName = user?.full_name || user?.email || 'this user';

  const {
    data: allGroups = [],
    isLoading: isLoadingGroups,
    error: groupsError,
  } = useGroups(true);
  const {
    data: assignedGroups = [],
    isLoading: isLoadingAssigned,
    error: assignedError,
  } = useUserGroupMemberships(userId);
  const setMutation = useSetUserGroupMemberships();
  const { toast, showToast, hideToast } = useToastNotification();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialIds, setInitialIds] = useState<Set<string>>(new Set());

  // Seed selection from server data once per open.
  useEffect(() => {
    if (!open) return;
    if (isLoadingAssigned) return;
    const ids = new Set(assignedGroups.map((g) => g.id));
    setSelectedIds(ids);
    setInitialIds(ids);
    setSearchQuery('');
  }, [open, isLoadingAssigned, assignedGroups]);

  const assignedIds = useMemo(
    () => new Set(assignedGroups.map((g) => g.id)),
    [assignedGroups]
  );

  // Visible groups: all active, plus archived ones already assigned.
  const visibleGroups = useMemo(() => {
    return allGroups.filter((group) => {
      const archived = !!group.archived_at;
      if (!archived) return true;
      return assignedIds.has(group.id);
    });
  }, [allGroups, assignedIds]);

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return visibleGroups;
    return visibleGroups.filter((group) =>
      group.display_name.toLowerCase().includes(query)
    );
  }, [visibleGroups, searchQuery]);

  const sections = useMemo(() => {
    return getOrderedRoleKeys()
      .map((parentRole) => {
        const rows = filteredGroups.filter((g) => g.parent_role === parentRole);
        const assignedInSection = rows.filter((g) =>
          selectedIds.has(g.id)
        ).length;
        return { parentRole, rows, assignedInSection };
      })
      .filter((section) => section.rows.length > 0);
  }, [filteredGroups, selectedIds]);

  const diffCount = useMemo(() => {
    let count = 0;
    for (const id of selectedIds) if (!initialIds.has(id)) count++;
    for (const id of initialIds) if (!selectedIds.has(id)) count++;
    return count;
  }, [selectedIds, initialIds]);

  const toggleGroup = (group: ManageGroup) => {
    const isAssignedArchived = !!group.archived_at && assignedIds.has(group.id);
    if (isAssignedArchived) return; // locked
    if (group.archived_at) return; // never newly checkable

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(group.id)) {
        next.delete(group.id);
      } else {
        next.add(group.id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!userId) return;
    try {
      await setMutation.mutateAsync({
        userId,
        groupIds: [...selectedIds],
      });
      showToast({
        title: ManageUsersContent.groups.drawer.successTitle,
        description:
          ManageUsersContent.groups.drawer.successDescription(userName),
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: ManageUsersContent.groups.drawer.errorTitle,
        description:
          err instanceof Error
            ? err.message
            : 'Failed to update groups. Please try again.',
        variant: 'error',
      });
    }
  };

  const isLoading = isLoadingGroups || isLoadingAssigned;
  const loadError = groupsError || assignedError;
  const saving = setMutation.isPending;
  const content = ManageUsersContent.groups.drawer;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle>{content.title}</DrawerTitle>
            <div className="flex gap-2 rounded-lg bg-purple-300/15 p-3 text-small text-purple-200">
              <InfoIcon
                size={16}
                weight="bold"
                className="mt-0.5 shrink-0 text-purple-300"
              />
              <DrawerDescription className="text-small text-purple-200">
                {content.description}
              </DrawerDescription>
            </div>
          </DrawerHeader>

          <div className="mb-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={content.searchPlaceholder}
              className="[&_input]:border-slate-500 [&_input]:bg-slate-600 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500"
            />
          </div>

          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-5 w-32 rounded-full bg-slate-600" />
                    <Skeleton className="h-12 w-full rounded-lg bg-slate-600" />
                    <Skeleton className="h-12 w-full rounded-lg bg-slate-600" />
                  </div>
                ))}
              </div>
            ) : loadError ? (
              <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-3 text-body text-red-100">
                {loadError instanceof Error
                  ? loadError.message
                  : content.loadError}
              </div>
            ) : sections.length === 0 ? (
              <div className="rounded-lg border border-slate-600 bg-slate-600/40 px-4 py-8 text-center text-body text-slate-400">
                {searchQuery.trim() ? content.searchEmpty : content.empty}
              </div>
            ) : (
              <div className="space-y-4">
                {sections.map((section) => (
                  <GroupSection
                    key={section.parentRole}
                    parentRole={section.parentRole}
                    rows={section.rows}
                    assignedInSection={section.assignedInSection}
                    selectedIds={selectedIds}
                    assignedIds={assignedIds}
                    onToggle={toggleGroup}
                  />
                ))}
              </div>
            )}
          </div>

          <DrawerFooter className="pt-4">
            <Button
              size="lg"
              onClick={handleSave}
              disabled={saving || diffCount === 0}
            >
              {saving
                ? content.saving
                : diffCount === 0
                  ? content.saveNoChanges
                  : content.save(diffCount)}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" size="lg" disabled={saving}>
                {content.cancel}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </Drawer>
  );
}

interface GroupSectionProps {
  parentRole: UserRole;
  rows: ManageGroup[];
  assignedInSection: number;
  selectedIds: Set<string>;
  assignedIds: Set<string>;
  onToggle: (group: ManageGroup) => void;
}

function GroupSection({
  parentRole,
  rows,
  assignedInSection,
  selectedIds,
  assignedIds,
  onToggle,
}: GroupSectionProps) {
  const tone = getGroupRoleTone(parentRole);
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[10px] uppercase text-slate-300',
            tone.pillClassName
          )}
        >
          <span className="h-1 w-1 rounded-full bg-current" />
          {getRoleLabelByKey(parentRole)}
        </span>
        <span className="text-small text-slate-500">
          {ManageUsersContent.groups.drawer.sectionCount(
            assignedInSection,
            rows.length
          )}
        </span>
      </div>
      <div className="overflow-hidden rounded-lg bg-slate-600">
        {rows.map((group, index) => {
          const isChecked = selectedIds.has(group.id);
          const isArchived = !!group.archived_at;
          const isLocked = isArchived && assignedIds.has(group.id);
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => onToggle(group)}
              disabled={isArchived}
              className={cn(
                'flex w-full items-start gap-3 px-3 py-3 text-left transition-colors',
                index > 0 && 'border-t border-slate-550/40',
                !isArchived && 'hover:bg-slate-500/30',
                isArchived && 'opacity-80'
              )}
            >
              {isLocked ? (
                <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-amber-400/40 text-amber-300">
                  <LockSimpleIcon size={11} weight="bold" />
                </div>
              ) : (
                <span className="mt-0.5 shrink-0">
                  <Checkbox checked={isChecked} disabled={isArchived} />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'truncate text-body',
                      isLocked ? 'text-slate-300' : 'text-slate-50'
                    )}
                  >
                    {group.display_name}
                  </span>
                  {isLocked && (
                    <span className="inline-flex shrink-0 items-center rounded-full border border-amber-400/40 bg-amber-400/15 px-1.5 py-0.5 font-field-42 text-[9px] uppercase text-amber-300">
                      {ManageUsersContent.groups.drawer.archivedReadOnly}
                    </span>
                  )}
                </div>
                <code className="mt-0.5 block truncate font-mono text-[11px] text-slate-500">
                  {group.import_key}
                </code>
              </div>
              <span className="shrink-0 self-center text-small text-slate-500">
                {group.member_count}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
